import React, { useState, useEffect } from 'react';
import { Card, Heading, Text, Flex, Avatar, Separator, Box } from '@radix-ui/themes';
import { StarIcon, TargetIcon, CheckIcon, CalendarIcon, CircleIcon } from '@radix-ui/react-icons';
import { Member, FILE_SERVER_URL, apiService, CompetitionRecord, CompetitionResponse } from './services/api';
import AppBar from './components/AppBar';
import NavigationDrawer from './components/NavigationDrawer';

interface AchievementsProps {
    students: Member[];
    selectedStudent: Member | null;
    onStudentChange: (student: Member) => void;
}

const Achievements: React.FC<AchievementsProps> = ({ students, selectedStudent, onStudentChange }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [competitionData, setCompetitionData] = useState<CompetitionResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!selectedStudent) return;

        setLoading(true);
        setError(null);

        apiService.getCompetitionDetails(selectedStudent.memberId)
            .then(response => {
                if (response.success) {
                    setCompetitionData(response);
                } else {
                    setError('Failed to fetch competition details');
                }
            })
            .catch(() => setError('Failed to fetch competition details'))
            .finally(() => setLoading(false));
    }, [selectedStudent]);

    // Calculate achievements statistics
    const calculateStats = () => {
        if (!competitionData?.competitions) return { totalCompetitions: 0, totalMedals: 0, participationRate: 0 };

        const competitions = competitionData.competitions;
        let totalMedals = 0;
        let totalEvents = 0;
        let participatedEvents = 0;

        competitions.forEach(comp => {
            // Count medals (only kata and kumite)
            if (comp.kata_res !== '0') totalMedals++;
            if (comp.kumite_res !== '0') totalMedals++;

            // Count participation (only kata and kumite)
            if (comp.kata === 1) {
                totalEvents++;
                participatedEvents++;
            }
            if (comp.kumite === 1) {
                totalEvents++;
                participatedEvents++;
            }
        });

        const participationRate = totalEvents > 0 ? Math.round((participatedEvents / totalEvents) * 100) : 0;

        return {
            totalCompetitions: competitions.length,
            totalMedals,
            participationRate
        };
    };

    // Get medal text and color
    const getMedalInfo = (result: string) => {
        switch (result) {
            case '1': return { text: 'Gold', color: 'var(--yellow-9)' };
            case '2': return { text: 'Silver', color: 'var(--gray-9)' };
            case '3': return { text: 'Bronze', color: 'var(--orange-9)' };
            default: return { text: 'No Medal', color: 'var(--gray-6)' };
        }
    };

    // Format date to proper format
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Get participation text
    const getParticipationText = (comp: CompetitionRecord) => {
        const events = [];
        if (comp.kata === 1) events.push('Kata');
        if (comp.kumite === 1) events.push('Kumite');
        return events.join(', ') || 'No Events';
    };

    // Get event results text
    const getEventResults = (comp: CompetitionRecord) => {
        const results = [];
        if (comp.kata === 1) {
            const kataMedal = getMedalInfo(comp.kata_res);
            results.push(`Kata: ${kataMedal.text}`);
        }
        if (comp.kumite === 1) {
            const kumiteMedal = getMedalInfo(comp.kumite_res);
            results.push(`Kumite: ${kumiteMedal.text}`);
        }
        return results.join(' • ') || 'No Results';
    };

    const stats = calculateStats();

    return (
        <div className="home-page">
            <AppBar
                onMenuClick={() => setIsDrawerOpen(true)}
                students={students}
                selectedStudent={selectedStudent}
                onStudentChange={onStudentChange}
            />
            <NavigationDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onLogout={() => window.location.href = '/'}
                selectedStudent={selectedStudent}
            />
            <main className="home-content">
                <div className="content-container">
                    {/* Welcome Section */}
                    <Card className="welcome-card">
                        <Flex align="center" gap="4">
                            <Avatar
                                size="4"
                                src={selectedStudent?.avatar && selectedStudent?.avatar !== 'NoImageFound.png' ? `${FILE_SERVER_URL}/show_files/${selectedStudent.avatar}` : undefined}
                                fallback={selectedStudent?.fullName?.charAt(0) || 'S'}
                            />
                            <Flex direction="column" gap="1">
                                <Heading size="6">Welcome, {selectedStudent?.fullName || 'Student'}!</Heading>
                                <Text size="2" color="gray">Here are your achievements and accomplishments.</Text>
                            </Flex>
                        </Flex>
                    </Card>

                    {/* Stats Section */}
                    <div className="profile-stats-grid">
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="blue">
                                    {stats.totalCompetitions}
                                </Text>
                                <Text size="2" color="gray">Total Competitions</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="green">
                                    {stats.totalMedals}
                                </Text>
                                <Text size="2" color="gray">Total Medals</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="orange">
                                    {stats.participationRate}%
                                </Text>
                                <Text size="2" color="gray">Participation Rate</Text>
                            </Flex>
                        </Card>
                    </div>

                    {/* Recent Achievements */}
                    <Card className="activity-card">
                        <Heading size="4" mb="4">Recent Competitions</Heading>
                        {loading ? (
                            <Text size="3">Loading competition details...</Text>
                        ) : error ? (
                            <Text size="3" color="red">{error}</Text>
                        ) : competitionData?.competitions ? (
                            <Flex direction="column" gap="3">
                                {competitionData.competitions.slice(0, 5).map((comp, index) => {
                                    const participation = getParticipationText(comp);
                                    const eventResults = getEventResults(comp);

                                    return (
                                        <Flex key={index} align="center" gap="3" className="activity-item">
                                            <div className="activity-icon">
                                                <CircleIcon width="16" height="16" />
                                            </div>
                                            <Flex direction="column" gap="1">
                                                <Text size="2" weight="bold">{comp.comp_name}</Text>
                                                <Text size="1" color="gray">{formatDate(comp.comp_date)} • {participation}</Text>
                                                <Text size="1" color="gray" style={{ fontStyle: 'italic' }}>{eventResults}</Text>
                                            </Flex>
                                            <Flex gap="2" style={{ marginLeft: 'auto' }}>
                                                {comp.kata === 1 && comp.kata_res !== '0' && (
                                                    <div className="activity-icon" style={{ backgroundColor: getMedalInfo(comp.kata_res).color }}>
                                                        <CircleIcon width="12" height="12" style={{ color: 'white' }} />
                                                    </div>
                                                )}
                                                {comp.kumite === 1 && comp.kumite_res !== '0' && (
                                                    <div className="activity-icon" style={{ backgroundColor: getMedalInfo(comp.kumite_res).color }}>
                                                        <CircleIcon width="12" height="12" style={{ color: 'white' }} />
                                                    </div>
                                                )}
                                            </Flex>
                                        </Flex>
                                    );
                                })}
                            </Flex>
                        ) : (
                            <Text size="3">No competition data available</Text>
                        )}
                    </Card>

                    {/* Achievement Categories - Temporarily Disabled */}
                    {/* <Card className="activity-card">
                        <Heading size="4" mb="4">Achievement Categories</Heading>
                        {loading ? (
                            <Text size="3">Loading achievement categories...</Text>
                        ) : error ? (
                            <Text size="3" color="red">{error}</Text>
                        ) : competitionData?.competitions ? (
                            <Flex direction="column" gap="3">
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <CircleIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Kata Achievements</Text>
                                        <Text size="1" color="gray">
                                            {competitionData.competitions.filter(c => c.kata === 1).length} competitions
                                        </Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--blue-9)', marginLeft: 'auto' }}>
                                        <CircleIcon width="16" height="16" />
                                    </div>
                                </Flex>
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <CircleIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Kumite Achievements</Text>
                                        <Text size="1" color="gray">
                                            {competitionData.competitions.filter(c => c.kumite === 1).length} competitions
                                        </Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--green-9)', marginLeft: 'auto' }}>
                                        <CircleIcon width="16" height="16" />
                                    </div>
                                </Flex>
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <CircleIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Overall Performance</Text>
                                        <Text size="1" color="gray">{stats.participationRate}% participation rate</Text>
                                    </Flex>
                                    <div className="activity-icon" style={{ backgroundColor: 'var(--orange-9)', marginLeft: 'auto' }}>
                                        <CircleIcon width="16" height="16" />
                                    </div>
                                </Flex>
                            </Flex>
                        ) : (
                            <Text size="3">No achievement data available</Text>
                        )}
                    </Card> */}
                </div>
            </main>
        </div>
    );
};

export default Achievements; 